import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { trackPageView, trackEvent } from './analytics'

const BUTTON_DEBOUNCE_MS = 300
const FORM_DEBOUNCE_MS = 500

const getButtonLabel = (target) => {
  if (!target) return 'Unknown Button'
  const el = target.closest('button, [role="button"]')
  if (!el) return 'Unknown Button'
  return (
    el.getAttribute('data-analytics-label') ||
    el.getAttribute('aria-label') ||
    el.innerText?.trim() ||
    el.name ||
    el.id ||
    'Unnamed Button'
  )
}

const getFormLabel = (target) => {
  if (!target) return 'Unknown Form'
  const form = target.closest('form')
  if (!form) return 'Unknown Form'
  return (
    form.getAttribute('data-analytics-label') ||
    form.getAttribute('aria-label') ||
    form.name ||
    form.id ||
    'Unnamed Form'
  )
}

const AnalyticsTracker = () => {
  const location = useLocation()
  const lastPathRef = useRef(null)

  useEffect(() => {
    const path = location.pathname + location.search
    if (lastPathRef.current === path) {
      return
    }
    lastPathRef.current = path
    trackPageView(path)
  }, [location])

  useEffect(() => {
    const buttonClickTimes = new WeakMap()
    const formSubmitTimes = new WeakMap()

    const handleClick = (event) => {
      const target = event.target
      const buttonEl = target.closest('button, [role="button"]')
      if (!buttonEl) return

      const now = Date.now()
      const lastTime = buttonClickTimes.get(buttonEl) || 0
      if (now - lastTime < BUTTON_DEBOUNCE_MS) return
      buttonClickTimes.set(buttonEl, now)

      const label = getButtonLabel(buttonEl)
      trackEvent('Button', 'click', label)
    }

    const handleSubmit = (event) => {
      const form = event.target
      if (!form || form.nodeName !== 'FORM') return

      const now = Date.now()
      const lastTime = formSubmitTimes.get(form) || 0
      if (now - lastTime < FORM_DEBOUNCE_MS) return
      formSubmitTimes.set(form, now)

      const label = getFormLabel(form)
      trackEvent('Form', 'submit', label)
    }

    if (typeof document !== 'undefined') {
      document.addEventListener('click', handleClick, true)
      document.addEventListener('submit', handleSubmit, true)
    }

    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('click', handleClick, true)
        document.removeEventListener('submit', handleSubmit, true)
      }
    }
  }, [])

  return null
}

export default AnalyticsTracker

